const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/** Backend origin without `/api` — used to resolve `/media/...` URLs. */
export const getApiOrigin = () => {
  const base = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  return String(base).replace(/\/api\/?$/, '');
};

/**
 * Normalize stored file URLs so PDFs/videos load from the real API host in production.
 * Rewrites localhost / relative `/media/` paths to REACT_APP_API_URL origin.
 * Appends a short-lived media access token (?m=), never the DRF auth token.
 */
export const resolveMediaUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  const origin = getApiOrigin();
  let resolved = url;
  try {
    if (url.startsWith('/')) {
      resolved = `${origin}${url}`;
    } else {
      const parsed = new URL(url);
      if (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') {
        resolved = `${origin}${parsed.pathname}${parsed.search}${parsed.hash}`;
      }
    }
  } catch {
    return url;
  }

  try {
    const parsed = new URL(resolved, origin);
    const isMedia =
      parsed.pathname.includes('/media/') ||
      parsed.pathname.startsWith('/media/');
    if (!isMedia) return resolved;
    const mediaToken = getMediaAccessToken();
    if (!mediaToken) return resolved;
    parsed.searchParams.delete('token');
    parsed.searchParams.delete('auth_token');
    if (!parsed.searchParams.has('m') && !parsed.searchParams.has('media_token')) {
      parsed.searchParams.set('m', mediaToken);
    }
    return parsed.toString();
  } catch {
    return resolved;
  }
};

// Token storage - using a module-level variable that can be set by AuthContext
let currentAuthToken = null;
let currentMediaAccessToken = null;

export const setAuthToken = (token) => {
  currentAuthToken = token;
};

export const getAuthToken = () => {
  // Try to get from sessionStorage as fallback (for API calls outside React context)
  return currentAuthToken || sessionStorage.getItem('authToken');
};

export const setMediaAccessToken = (token) => {
  currentMediaAccessToken = token || null;
  if (token) {
    sessionStorage.setItem('mediaAccessToken', token);
  } else {
    sessionStorage.removeItem('mediaAccessToken');
  }
};

export const getMediaAccessToken = () => {
  return currentMediaAccessToken || sessionStorage.getItem('mediaAccessToken');
};

/** List endpoints return a plain array by default; with `?page=` they return `{ results, count, next, previous }`. */
export const unwrapListResponse = (data) => {
  if (data && typeof data === 'object' && !Array.isArray(data) && Array.isArray(data.results)) {
    return data.results;
  }
  return Array.isArray(data) ? data : [];
};

// Make API request with authentication
const apiRequest = async (endpoint, options = {}) => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Token ${token}`;
  }

  const config = {
    ...options,
    headers,
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    
    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.error || errorMessage;
      } catch (e) {
        // If response is not JSON, use status text
        errorMessage = response.statusText || errorMessage;
      }
      // On 401 (e.g. Invalid token), clear auth so user can log in again
      if (response.status === 401) {
        currentAuthToken = null;
        currentMediaAccessToken = null;
        try {
          sessionStorage.removeItem('authToken');
          sessionStorage.removeItem('userData');
          sessionStorage.removeItem('mediaAccessToken');
        } catch (e) { /* ignore */ }
        window.dispatchEvent(new Event('auth-session-expired'));
      }
      throw new Error(errorMessage);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// Employee API
export const employeeAPI = {
  getAll: (params = {}) => {
    const qs = new URLSearchParams(params);
    const q = qs.toString();
    const path = q ? `/auth/employees/?${q}` : '/auth/employees/';
    return apiRequest(path).then(unwrapListResponse);
  },
  getById: (id) => apiRequest(`/auth/employees/${id}/`),
  create: (data) => apiRequest('/auth/employees/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiRequest(`/auth/employees/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiRequest(`/auth/employees/${id}/`, {
    method: 'DELETE',
  }),
  sendPasswordResetToActive: () => apiRequest('/auth/send-password-reset-to-active-employees/', {
    method: 'POST',
    body: JSON.stringify({ confirm: 'SEND_PASSWORD_RESET_ALL' }),
  }),
};

export const departmentAPI = {
  getAll: () => apiRequest('/auth/departments/'),
  create: (data) => apiRequest('/auth/departments/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiRequest(`/auth/departments/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiRequest(`/auth/departments/${id}/`, {
    method: 'DELETE',
  }),
};

export const skillCatalogAPI = {
  getAll: () => apiRequest('/auth/skills-catalog/'),
  create: (data) => apiRequest('/auth/skills-catalog/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiRequest(`/auth/skills-catalog/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiRequest(`/auth/skills-catalog/${id}/`, {
    method: 'DELETE',
  }),
};

export const sessionTypeAPI = {
  getAll: () => apiRequest('/auth/session-types/').then(unwrapListResponse),
  create: (data) => apiRequest('/auth/session-types/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiRequest(`/auth/session-types/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiRequest(`/auth/session-types/${id}/`, {
    method: 'DELETE',
  }),
};

export const participantGroupAPI = {
  getAll: () => apiRequest('/auth/participant-groups/').then(unwrapListResponse),
  getById: (id) => apiRequest(`/auth/participant-groups/${id}/`),
  create: (data) => apiRequest('/auth/participant-groups/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiRequest(`/auth/participant-groups/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiRequest(`/auth/participant-groups/${id}/`, {
    method: 'DELETE',
  }),
};

// Session API
export const sessionAPI = {
  getAll: (status = null, more = {}) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    Object.entries(more).forEach(([k, v]) => {
      if (v != null && v !== '') params.set(k, String(v));
    });
    const qs = params.toString();
    const endpoint = qs ? `/auth/sessions/?${qs}` : '/auth/sessions/';
    return apiRequest(endpoint).then(unwrapListResponse);
  },
  getById: (id) => apiRequest(`/auth/sessions/${id}/`),
  create: (data) => apiRequest('/auth/sessions/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiRequest(`/auth/sessions/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiRequest(`/auth/sessions/${id}/`, {
    method: 'DELETE',
  }),
  clearAll: () => apiRequest('/auth/sessions/clear-all/', {
    method: 'POST',
    body: JSON.stringify({ confirm: 'DELETE_ALL_SESSIONS' }),
  }),
  syncCalendar: (sessionId) => apiRequest(`/auth/sessions/${sessionId}/sync-calendar/`, {
    method: 'POST',
  }),
  recordJoin: (sessionId) => apiRequest(`/auth/sessions/${sessionId}/join/`, {
    method: 'POST',
  }),
  rescheduleEmployee: (sessionId, employeeId) => apiRequest(`/auth/sessions/${sessionId}/reschedule-employee/`, {
    method: 'POST',
    body: JSON.stringify({ employee: employeeId }),
  }),
};

/** HR admin attendance reports (SessionAttendance vs eligible employees). */
export const reportsAPI = {
  getSessionAttendance: (params = {}) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') search.set(k, String(v));
    });
    const qs = search.toString();
    return apiRequest(
      qs ? `/auth/reports/session-attendance/?${qs}` : '/auth/reports/session-attendance/'
    );
  },
};

// Session Request API
export const sessionRequestAPI = {
  getAll: (status = null, more = {}) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    Object.entries(more).forEach(([k, v]) => {
      if (v != null && v !== '') params.set(k, String(v));
    });
    const qs = params.toString();
    const endpoint = qs ? `/auth/session-requests/?${qs}` : '/auth/session-requests/';
    return apiRequest(endpoint).then(unwrapListResponse);
  },
  getById: (id) => apiRequest(`/auth/session-requests/${id}/`),
  create: (data) => apiRequest('/auth/session-requests/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiRequest(`/auth/session-requests/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => apiRequest(`/auth/session-requests/${id}/`, {
    method: 'DELETE',
  }),
};

// Session Completion API
export const sessionCompletionAPI = {
  getAll: (employeeId = null, sessionId = null, more = {}) => {
    const params = new URLSearchParams();
    if (employeeId) params.append('employee', employeeId);
    if (sessionId) params.append('session', sessionId);
    Object.entries(more).forEach(([k, v]) => {
      if (v != null && v !== '') params.set(k, String(v));
    });
    const queryString = params.toString();
    const endpoint = queryString
      ? `/auth/session-completions/?${queryString}`
      : '/auth/session-completions/';
    return apiRequest(endpoint).then(unwrapListResponse);
  },
  create: (data) => apiRequest('/auth/session-completions/', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => apiRequest(`/auth/session-completions/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// Auth API (login does not use token; uses API_BASE_URL so it works in production)
export const authAPI = {
  login: (credentials) =>
    fetch(`${API_BASE_URL}/auth/login/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials),
    }).then(async (res) => {
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Invalid credentials');
      }
      return res.json();
    }),
  logout: () => apiRequest('/auth/logout/', {
    method: 'POST',
  }),
};

// Analytics API
export const analyticsAPI = {
  getAnalytics: () => apiRequest('/auth/analytics/'),
};

// Notifications API
export const notificationAPI = {
  getAll: (more = {}) => {
    const params = new URLSearchParams();
    Object.entries(more).forEach(([k, v]) => {
      if (v != null && v !== '') params.set(k, String(v));
    });
    const qs = params.toString();
    const endpoint = qs ? `/auth/notifications/?${qs}` : '/auth/notifications/';
    return apiRequest(endpoint).then(unwrapListResponse);
  },
  getById: (id) => apiRequest(`/auth/notifications/${id}/`),
  markAllRead: () => apiRequest('/auth/notifications/mark-read/', {
    method: 'POST',
  }),
  update: (id, data) => apiRequest(`/auth/notifications/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
};

// Google Calendar API (for employees: connect calendar so scheduled sessions appear)
export const googleCalendarAPI = {
  getAuthUrl: () => apiRequest('/auth/google-calendar/auth-url/'),
  getStatus: () => apiRequest('/auth/google-calendar/status/'),
  disconnect: () => apiRequest('/auth/google-calendar/disconnect/', {
    method: 'POST',
  }),
  // Admin only: disconnect another user's Google Calendar
  disconnectUser: (userId) => apiRequest(`/auth/google-calendar/disconnect/${userId}/`, {
    method: 'POST',
  }),
};

/**
 * Upload PPT/PPTX file for server-side conversion to slide images.
 * Returns { jobId, slides: [url, ...], count }.
 * Employees must pass sessionId (invited session). Admins may omit it.
 * Do not set Content-Type so browser sends multipart/form-data.
 */
export const uploadPptxForSlides = async (file, sessionId = null) => {
  const token = getAuthToken();
  const formData = new FormData();
  formData.append('file', file, file.name || 'presentation.pptx');
  if (sessionId != null && sessionId !== '') {
    formData.append('session', String(sessionId));
  }
  const headers = {};
  if (token) headers['Authorization'] = `Token ${token}`;
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  const response = await fetch(`${baseUrl}/auth/render-pptx/`, {
    method: 'POST',
    headers,
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data?.error || response.statusText || 'Upload/render failed');
    err.status = response.status;
    err.conversionUnavailable = Boolean(data?.conversion_unavailable);
    err.officeViewerFallback = Boolean(data?.office_viewer_fallback);
    err.payload = data;
    throw err;
  }
  return data;
};

/**
 * Upload training media (video/PDF/PPT) to server MEDIA for in-browser playback.
 * Returns { id, name, size, type, url }.
 */
export const uploadSessionFile = async (file) => {
  const token = getAuthToken();
  const formData = new FormData();
  formData.append('file', file, file.name || 'upload.bin');
  const headers = {};
  if (token) headers['Authorization'] = `Token ${token}`;
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  let response;
  try {
    response = await fetch(`${baseUrl}/auth/upload-session-file/`, {
      method: 'POST',
      headers,
      body: formData,
    });
  } catch (err) {
    throw new Error(
      'Upload failed or timed out. Check nginx/gunicorn timeouts and network, then retry.'
    );
  }
  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    data = null;
  }
  if (!response.ok) {
    if (response.status === 413) {
      throw new Error('File is too large for the server (413). Increase client_max_body_size.');
    }
    if (response.status === 504 || response.status === 502) {
      throw new Error(
        'Upload timed out at the proxy (502/504). Increase nginx proxy_*_timeout and gunicorn --timeout.'
      );
    }
    throw new Error(data?.error || response.statusText || 'File upload failed');
  }
  return data;
};

export default apiRequest;

